/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as Core from 'app/core/models/essette-api';
import { DocumentsService } from 'app/core/services/documents-service';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import { deepClone } from 'app/shared/lib/utilities';

@autoinject()
export class SaveDocumentCategory extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public documentsConfig?: IDocumentsConfig;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public documentCategory: Core.DocumentCategory = {};

    protected documentCategoryFlags: Core.DocumentFlag[] = [];
    protected documentCategoryText: string = '';
    protected selectedDocumentCategoryFlags: Core.DocumentFlag[] = [];

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: DocumentsService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.documentsConfig) { throw new Error('Bindable property [documents-config] was not set'); }

        this.service.configure(this.documentsConfig);

    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    private async documentCategoryChanged(): Promise<void> {
        await this.loadLookups();
        if(!this.documentCategory) return;

        // Params for documentCategory info
        this.documentCategoryText = this.documentCategory.description || '';
        this.selectedDocumentCategoryFlags = this.documentCategory.flags ? deepClone(this.documentCategory.flags) : [];
    }

    protected buildDocumentCategory(): Core.DocumentCategory {
        let flagIds: Core.DocumentFlag[] = [];
        this.selectedDocumentCategoryFlags.forEach(flag => flagIds.push({ id: flag.id }));

        const saveDocumentCategory: Core.DocumentCategory = {
            id: this.documentCategory?.id || null,
            description: this.documentCategoryText || undefined,
            flags: flagIds
        };

        return saveDocumentCategory;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Document not implemented.');
    }

    public async submit(): Promise<boolean> {
        return await this.saveDocumentCategory();
    }

    public reset(): void {
        this.documentCategoryText = '';
        this.selectedDocumentCategoryFlags = [];
        this.documentCategory = {};
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadDocumentFlags(options);
            this.documentCategoryFlags = response.results;

        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveDocumentCategory(): Promise<boolean> {
        const insertDocumentCategory: Core.DocumentCategory = this.buildDocumentCategory();

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.saveDocumentCategory(insertDocumentCategory);
            isSuccessful = response.isSuccessful;

            return isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('core:document.category.attached.$long', isSuccessful);
            this.state.endSave();
        }
    }
}
